"use client";

import { useState } from "react";
import { Popover } from "@base-ui-components/react/popover";
import { Icon } from "@/components/ui/icon";
import { formatYearMonthShort } from "@/lib/date/date";
import { toYearMonthKey } from "@/lib/date/day-keys";
import { cn } from "@/lib/utils";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const popupClassName = cn(
  "z-50 w-auto rounded-lg border border-contrast/1 bg-surface-elevated p-0 text-heading outline-none",
  "shadow-[0px_1px_1px_-0.5px_rgba(0,0,0,0.04),0px_3px_3px_-1.5px_rgba(0,0,0,0.04),0px_6px_6px_-3px_rgba(0,0,0,0.04),0px_12px_12px_-6px_rgba(0,0,0,0.04),0px_0px_0px_1px_rgb(from_var(--contrast)_r_g_b_/_0.1)]",
  "transition-opacity data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
);

function MonthYearGrid({
  value,
  onSelect,
}: {
  value?: Date;
  onSelect: (date: Date) => void;
}) {
  const now = new Date();
  const [year, setYear] = useState(value?.getFullYear() ?? now.getFullYear());
  const selectedMonth = value?.getMonth();
  const selectedYear = value?.getFullYear();

  return (
    <div className="w-[180px] px-2.5 py-2">
      <div className="mb-1.5 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setYear((y) => y - 1)}
          className="flex size-[22px] cursor-pointer items-center justify-center rounded hover:bg-contrast/10"
        >
          <Icon
            name="IconArrowLeft"
            size={12}
            className="text-foreground opacity-50"
          />
        </button>
        <span className="select-none text-[11px] font-medium tracking-[-0.1px] text-foreground/80">
          {year}
        </span>
        <button
          type="button"
          onClick={() => setYear((y) => (y < now.getFullYear() ? y + 1 : y))}
          disabled={year >= now.getFullYear()}
          className="flex size-[22px] cursor-pointer items-center justify-center rounded hover:bg-contrast/10 disabled:cursor-not-allowed disabled:opacity-20"
        >
          <Icon
            name="IconArrowRight"
            size={12}
            className="text-foreground opacity-50"
          />
        </button>
      </div>
      <div className="grid grid-cols-4 gap-0.5">
        {MONTHS.map((label, i) => {
          const isFuture = year === now.getFullYear() && i > now.getMonth();
          const isSelected = selectedYear === year && selectedMonth === i;
          return (
            <button
              key={label}
              type="button"
              disabled={isFuture}
              onClick={() => onSelect(new Date(year, i, 1))}
              className={cn(
                "h-6 cursor-pointer rounded text-[10px] font-medium transition-colors",
                isSelected
                  ? "bg-ai/20 text-ai"
                  : "text-foreground/50 hover:bg-contrast/6 hover:text-foreground/70",
                isFuture && "cursor-not-allowed opacity-20",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FilterMonthYearInput({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (date: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const parsed = value?.split("-");
  const selectedDate =
    parsed?.[0] && parsed?.[1]
      ? new Date(Number(parsed[0]), Number(parsed[1]) - 1, 1)
      : undefined;

  const handleSelect = (date: Date) => {
    onChange(toYearMonthKey(date));
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={
          <button
            type="button"
            className="flex cursor-pointer items-center gap-0.5"
          >
            {selectedDate ? (
              <span className="whitespace-nowrap text-[11px] tracking-[-0.1px] text-ai">
                {formatYearMonthShort(selectedDate)}
              </span>
            ) : (
              <span className="whitespace-nowrap text-[11px] tracking-[-0.1px] text-ai/60">
                Date
              </span>
            )}
            <Icon
              name="IconChevronDownMedium"
              size={11}
              className="text-ai"
            />
          </button>
        }
      />
      <Popover.Portal>
        <Popover.Positioner align="start" sideOffset={6}>
          <Popover.Popup className={popupClassName}>
            <MonthYearGrid value={selectedDate} onSelect={handleSelect} />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
